import { adminApiClient, type AdminApiClient } from './client';

export interface Asset {
  id: string;
  name: string;
  type: 'image/png' | 'image/jpeg' | 'image/svg+xml' | 'image/webp';
  tags: string[];
  blobPath: string;
  previewUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssetBatchUploadFailure {
  filename: string;
  message: string;
}

export interface AssetBatchUploadResult {
  assets: Asset[];
  failures: AssetBatchUploadFailure[];
}

export interface AssetBatchDeleteFailure {
  id: string;
  message: string;
}

export interface AssetBatchDeleteResult {
  deletedCount: number;
  failures: AssetBatchDeleteFailure[];
}

export interface AssetsApi {
  list: (input: { query?: string; tag?: string }) => Promise<Asset[]>;
  upload: (input: { file: File; name: string; tags: string[] }) => Promise<Asset>;
  uploadBatch: (input: {
    files: File[];
    names: string[];
    tags: string[];
  }) => Promise<AssetBatchUploadResult>;
  update: (input: { id: string; name: string; tags: string[] }) => Promise<Asset>;
  delete: (id: string) => Promise<void>;
  deleteBatch: (ids: string[]) => Promise<AssetBatchDeleteResult>;
}

export const createAssetsApi = (client: AdminApiClient = adminApiClient): AssetsApi => ({
  list: async (input) => {
    const params = new URLSearchParams();
    if (input.query) params.set('q', input.query);
    if (input.tag) params.set('tag', input.tag);

    const query = params.toString();
    return client.request<Asset[]>(`/admin/assets${query ? `?${query}` : ''}`);
  },
  upload: async (input) => {
    const form = new FormData();
    form.set('file', input.file);
    form.set('name', input.name);
    form.set('tags', input.tags.join(','));

    return client.request<Asset>('/admin/assets', {
      method: 'POST',
      body: form,
    });
  },
  uploadBatch: async (input) => {
    const form = new FormData();
    for (const file of input.files) {
      form.append('files', file);
    }
    form.set('names', JSON.stringify(input.names));
    form.set('tags', input.tags.join(','));

    return client.request<AssetBatchUploadResult>('/admin/assets/batch', {
      method: 'POST',
      body: form,
    });
  },
  update: async (input) =>
    client.request<Asset>(`/admin/assets/${input.id}`, {
      method: 'PATCH',
      body: { name: input.name, tags: input.tags },
    }),
  delete: async (id) => {
    await client.request<void>(`/admin/assets/${id}`, { method: 'DELETE' });
  },
  deleteBatch: async (ids) =>
    client.request<AssetBatchDeleteResult>('/admin/assets/batch/delete', {
      method: 'POST',
      body: { ids },
    }),
});

export const assetsApi = createAssetsApi();

export const listAssets = assetsApi.list;
export const uploadAsset = assetsApi.upload;
export const uploadAssetsBatch = assetsApi.uploadBatch;
export const updateAsset = assetsApi.update;
export const deleteAsset = assetsApi.delete;
export const deleteAssetsBatch = assetsApi.deleteBatch;
